/**
 * CODAM 사이트 SEO — Google·네이버 등 포털 검색용 메타 태그 공통 정의
 */

export const SITE_ORIGIN = (process.env.REACT_APP_SITE_URL || 'https://codam.co.kr').replace(/\/$/, '')

export const SITE_NAME = 'CODAM'
export const SITE_NAME_KO = '코담'

export const SITE_BROWSER_TITLE_TAGLINE =
    '프리미엄 엔터프라이즈 IT 솔루션 파트너 – 기업 웹 개발, AI 시스템 구축, 클라우드 인프라'

export const HOME_BROWSER_TITLE = `${SITE_NAME} | ${SITE_BROWSER_TITLE_TAGLINE}`

/** 검색 포털 공통 기본 키워드 */
export const SEO_BASE_KEYWORDS = [
    '코담',
    'CODAM',
    '기업 IT 솔루션',
    '웹 개발',
    '소프트웨어 개발',
    '맞춤형 개발',
    'AI 시스템 구축',
    '데이터 플랫폼',
    '클라우드 구축',
    '엔터프라이즈',
    '사이버 보안',
    '디지털 전환',
]

export const SEO_DEFAULT_DESCRIPTION =
    'CODAM(코담)은 기업 맞춤형 웹 플랫폼 구축, AI 기반 업무 자동화, 데이터 플랫폼, 클라우드 인프라, 사이버 보안, 제품·산업·캐릭터 디자인까지 엔터프라이즈 IT 솔루션을 제공합니다.'

export const SEO_DEFAULT_OG_IMAGE_PATH = '/assets/img/logo/codam-logo.png'

export const SEO_LOCALE = 'ko_KR'

/** 동적 상세 페이지 — RouteSeoManager가 건너뛰고 페이지 컴포넌트가 직접 SEO 설정 */
export const SEO_DYNAMIC_PATH_PATTERNS = [
    /^\/project-details\/[^/]+$/,
    /^\/notices\/[^/]+$/,
    /^\/codam-design\/product-design\/[^/]+$/,
    /^\/codam-design\/industrial-design\/[^/]+$/,
]

export function pageBrowserTitle(pageLabel) {
    return `${pageLabel} | ${SITE_NAME} – ${SITE_BROWSER_TITLE_TAGLINE}`
}

export function mergeSeoKeywords(...groups) {
    const seen = new Set()
    const out = []
    for (const group of groups) {
        if (!group) continue
        const items = Array.isArray(group) ? group : String(group).split(',')
        for (const raw of items) {
            const kw = raw.trim()
            if (!kw) continue
            const key = kw.toLowerCase()
            if (seen.has(key)) continue
            seen.add(key)
            out.push(kw)
        }
    }
    return out.join(', ')
}

export function absoluteUrl(pathname = '/') {
    const path = pathname.startsWith('/') ? pathname : `/${pathname}`
    return `${SITE_ORIGIN}${path}`
}

export function absoluteAssetUrl(assetPath) {
    if (!assetPath) return absoluteUrl(SEO_DEFAULT_OG_IMAGE_PATH)
    if (/^https?:\/\//i.test(assetPath)) return assetPath
    const path = assetPath.startsWith('/') ? assetPath : `/${assetPath}`
    return `${SITE_ORIGIN}${path}`
}

/**
 * @param {{
 *   pageLabel?: string,
 *   title?: string,
 *   description?: string,
 *   keywords?: string | string[],
 *   pathname?: string,
 *   image?: string,
 *   noindex?: boolean,
 *   isHome?: boolean,
 * }} options
 */
export function buildPageSeo(options = {}) {
    const {
        pageLabel,
        title: titleOverride,
        description = SEO_DEFAULT_DESCRIPTION,
        keywords,
        pathname = '/',
        image,
        noindex = false,
        isHome = false,
    } = options

    const title = titleOverride ?? (isHome ? HOME_BROWSER_TITLE : pageBrowserTitle(pageLabel || SITE_NAME_KO))
    const canonical = absoluteUrl(pathname)
    const ogImage = absoluteAssetUrl(image || SEO_DEFAULT_OG_IMAGE_PATH)
    const keywordString = mergeSeoKeywords(SEO_BASE_KEYWORDS, keywords)

    return {
        title,
        description,
        keywords: keywordString,
        canonical,
        ogImage,
        ogUrl: canonical,
        noindex,
        isHome,
        pathname,
    }
}

export function isDynamicSeoPath(pathname) {
    return SEO_DYNAMIC_PATH_PATTERNS.some((re) => re.test(pathname))
}
